import React, { useEffect, useState } from "react";
import { useHeldRows, heldNote } from "@/lib/heldRows";
import { useNavigate } from "react-router-dom";
import { ECON_TITLE } from "@/lib/labels";
import { SpreadCell, addGate } from "@/components/OpportunityTradeCells";
import { useApp } from "@/context/AppContext";
import api, { refusalText } from "@/lib/api";
import { inr, num } from "@/lib/format";
import { toast } from "sonner";
import { Crosshair, AlertTriangle, Bug, X, CheckCircle2 } from "lucide-react";
import { TradeConfigModal } from "@/pages/PaperTrading";
import { NfoOpportunities, NFO_STRATEGY_LABEL } from "@/pages/NfoPanel";
import { useAuth } from "@/context/AuthContext";
import { P } from "@/lib/permissions";
import { OpportunityInfo, OpportunityFilterBar, ExactSymbol, OiCell, PairCell, ActiveTradeHint } from "@/components/OpportunityInfo";
import { filterOpportunities, loadSavedFilters, saveFilters } from "@/lib/opportunityFilters";
import { useOpportunityPins } from "@/lib/opportunityPins";
import { PinnedOpportunities, PinToggle } from "@/components/PinnedOpportunities";
import { visibleInterval } from "@/lib/poll";
import RowStateBadge from "@/components/RowStateBadge";
import { ConvergeCell, MarginCell, ReturnCell, SCENARIO_TITLES, SpreadEntryCell } from "@/components/FuturesScenario";

function Inspector({ commodity, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/shadow/analysier/${commodity}/inspector`).then((r) => setD(r.data)).catch(() => {}); }, [commodity]);
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
      <div className="w-[560px] max-w-full h-full overflow-auto bg-[#12151C] border-l border-[#2A2F3A] p-5" onClick={(e) => e.stopPropagation()} data-testid="opp-inspector">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-bold flex items-center gap-2"><Bug className="h-4 w-4 text-[#5b9bff]" /> {commodity} · Strategy Inspector (V2.5)</div>
          <button onClick={onClose}><X className="h-4 w-4 text-slate-500" /></button>
        </div>
        {!d ? <div className="text-xs text-slate-500">Loading…</div> : (
          <div className="space-y-3">
            <div className="text-xs text-slate-400">Coeffs: funds {(d.coeffs.funds_pct*100).toFixed(1)}% · margin {(d.coeffs.margin_pct*100).toFixed(1)}% · lot {d.lot_size} · model {d.coeffs.spread_model}</div>
            <div className="text-xs font-bold uppercase text-slate-400">Decision Audit</div>
            {d.decision_audit.length === 0 ? <div className="text-xs text-slate-600">No live ticks yet — audit appears once data arrives.</div> :
              d.decision_audit.map((s) => (
                <div key={s.step} className="term-panel-2 rounded-sm p-2 text-xs">
                  <div className="font-semibold text-slate-200">{s.step}. {s.name}</div>
                  <pre className="mono text-[11px] text-slate-400 whitespace-pre-wrap mt-1">{JSON.stringify(Object.fromEntries(Object.entries(s).filter(([k]) => !["step","name"].includes(k))), null, 1)}</pre>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Section 2/3 — ADD TRADE: no slot selection. Picking an opportunity opens Trade
 * Configuration directly; Confirm & Add Trade creates a brand-new independent trade
 * instance (its own database id, its own paper capital) and it appears in Paper Trading
 * below the existing ones. The Excel worksheet's three Dashboard blocks were a spreadsheet
 * limitation and are not modelled here. */
/** The New Trade drawer's starting values (server: /paper/v25/config -> new_trade). Only a seed:
 *  every field is editable, and saved trades keep their own values. */
export function newTradeSeed(defaults) {
  const nt = (defaults || {}).new_trade;
  if (!nt) {
    return { name: "New Trade", allocated_funds: (defaults || {}).paper_capital_per_trade,
             edge_threshold: (defaults || {}).edge_threshold_default };
  }
  return { name: "New Trade", allocated_funds: nt.allocated_funds, trigger_pct: nt.trigger_pct,
           profit_pct: nt.profit_pct, edge_threshold: nt.edge_threshold,
           trading_environment: nt.default_environment || undefined };
}

const envLabel = (t) => (t?.trading_environment || "PAPER").toUpperCase();

/** The created trade, said as it is stored: its environment, its status, and that nothing was ordered. */
export function tradeCreatedNote(t) {
  return `${envLabel(t)} trade created as ${t?.status || "READY"} — not started, no orders placed. Press START on it when ready; ` +
    "START re-checks the contracts, direction and live prices, and entry happens only when the Dashboard conditions are met.";
}

/** W2 config warnings (paper_engine._resolve_opportunity, configure mode) in words. */
export function configWarningText(w) {
  const s = String(w);
  if (s.startsWith("MARKET_DATA_STALE_FOR_ENTRY:")) return `${s.split(":")[1]} leg quote older than the entry limit`;
  if (s.startsWith("DIRECTION_CHANGED:")) return `the live winning direction is now ${s.split(":")[1].replaceAll("_", " ")} (your selected direction is kept)`;
  if (s.startsWith("MARKET_DATA_")) return `market data ${s.slice(12).replaceAll("_", " ").toLowerCase()}`;
  return s;
}

function AddTradeModal({ opp, market, onClose, onCreated }) {
  const [created, setCreated] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const nav = useNavigate();
  const { can } = useAuth();
  const commodity = opp.commodity || opp.name;
  // Trade defaults come from the backend configuration — no hard-coded copies here.
  useEffect(() => {
    let live = true;
    api.get("/paper/v25/config").then((r) => { if (live) setDefaults(r.data || {}); })
      .catch(() => { if (live) setDefaults({}); });
    return () => { live = false; };
  }, []);

  if (created) {
    return (
      <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
        <div className="w-[520px] max-w-full h-full overflow-auto bg-[#12151C] border-l border-[#2A2F3A] p-5" onClick={(e) => e.stopPropagation()} data-testid="add-trade-confirmation">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-bold">Trade added</div>
            <button onClick={onClose}><X className="h-4 w-4 text-slate-500" /></button>
          </div>
          <div className="term-panel rounded-sm p-4 space-y-3">
            <div className="flex items-center gap-2 text-[#00C805] font-semibold text-sm">
              <CheckCircle2 className="h-4 w-4" /> {created.name} created · {commodity} · {envLabel(created)}
            </div>
            <div className="mono text-[10px] space-y-0.5">
              <div><span className="text-slate-500">SELL </span><span className="text-[#FF6b62]">{created.sell_symbol}</span></div>
              <div><span className="text-slate-500">BUY </span><span className="text-[#5b9bff]">{created.buy_symbol}</span></div>
            </div>
            <div className="text-[11px] text-slate-500" data-testid="add-trade-created-note">{tradeCreatedNote(created)}</div>
            {(created.config_warnings || []).length > 0 && (
              <div className="text-[11px] text-[#b38f00] flex gap-1.5" data-testid="add-trade-config-warnings">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>Not yet valid for entry: {created.config_warnings.map(configWarningText).join("; ")}. START re-checks these and refuses until they pass.</span>
              </div>)}
            <button onClick={() => nav(`/paper?open=${created.id}`)} data-testid="open-trade-btn"
              className="w-full text-sm font-bold py-2 rounded-sm bg-[#2563EB]">OPEN TRADE</button>
          </div>
        </div>
      </div>
    );
  }

  if (!defaults) return null;
  return (
    <TradeConfigModal
      mode="create"
      commodity={commodity}
      opportunity={{ ...opp, market_segment: market }}
      trade={newTradeSeed(defaults)}
      canLive={can(P.TRADING_LIVE)}
      environments={defaults.new_trade ? (defaults.new_trade.environments || []) : null}
      onClose={onClose}
      onConfirm={async (cfg) => {
        try {
          // What the user was shown: the backend re-validates it and refuses a stale or changed
          // opportunity instead of creating a trade.
          const expected = { sell_symbol: opp.sell_leg || opp.sell_symbol, buy_symbol: opp.buy_leg || opp.buy_symbol,
                             direction: opp.direction };
          const r = await api.post("/paper/v25/trades", {
            commodity, market, expected, pair_selection: opp.pair_selection || "NEAR_MID",
            opportunity_id: opp.opportunity_id || null, ...cfg });
          toast.success(`${commodity}: ${envLabel(r.data)} trade created (not started)`);
          setCreated(r.data);
          onCreated?.();
        } catch (e) {
          toast.error(refusalText(e, "Add trade failed"));
          throw e;          // keeps the modal open with the message shown in it
        }
      }}
    />
  );
}

// Default MCX columns, in decision order: identity, legs, economics, Add, prices, liquidity, Info.
// Spread, DTE, Lot, Funds (L), Margin (M), Status, DIR and Why live in the row's Info view.
const MCX_COLUMNS = [
  ["#", "text-left"], ["Commodity", "text-left"], ["Pair", "text-left"], ["SELL (exact)", "text-left"], ["BUY (exact)", "text-left"],
  ["Spread", "text-right"], ["Converge P&L", "text-right"], ["Margin", "text-right"], ["Return on margin", "text-right"],
  ["Near LTP", "text-right"], ["Far LTP", "text-right"], ["OI NEAR", "text-right"], ["OI FAR", "text-right"],
  ["Bid/Ask", "text-right"], ["Add", "text-left"], ["Info", "text-left"],
];

export default function Opportunities() {
  const [liveData, setData] = useState(null);
  const held = useHeldRows(liveData);       // W3: the table holds still under the pointer
  const data = held.rows;
  const [insp, setInsp] = useState(null);
  const [addOpp, setAddOpp] = useState(null);          // { opp, market } — no slots
  const [market, setMarket] = useState("MCX");
  // Profit % Min / OI Min — shared by MCX and NFO, remembered per viewer.
  const [filters, setFiltersState] = useState(loadSavedFilters);
  const pins = useOpportunityPins();
  const setFilters = (f) => { setFiltersState(f); saveFilters(f); };
  const nav = useNavigate();
  const { systemStatus } = useApp() || {};
  const mcxOpen = systemStatus?.market?.exchanges?.MCX?.open;      // undefined until the status loads

  useEffect(() => {
    const load = () => api.get("/shadow/analysier").then((r) => setData(r.data)).catch(() => {});
    load(); return visibleInterval(load, 3000);
  }, []);

  if (!data) return <div className="p-6 text-slate-500">Loading Opportunities…</div>;

  if (!data.zerodha_ready) {
    return (
      <div className="p-6">
        <div className="term-panel rounded-sm p-8 text-center" data-testid="opp-waiting">
          <AlertTriangle className="h-8 w-8 text-[#FFCC00] mx-auto mb-3" />
          <div className="text-sm font-semibold">OPPORTUNITIES · WAITING FOR ZERODHA</div>
          <div className="text-xs text-slate-500 mt-1">{data.message}</div>
          <div className="text-[11px] text-slate-600 mt-2">Opportunities are calculated from live Zerodha MCX data only — never from made-up prices.</div>
          <button onClick={() => nav("/broker")} className="mt-4 text-xs font-semibold px-3 py-1.5 rounded-sm bg-[#2563EB]">Go to Broker Session</button>
        </div>
      </div>
    );
  }

  const mcxShown = filterOpportunities(data.opportunities, filters);

  return (
    <div className="p-4 space-y-3">
      {insp && <Inspector commodity={insp} onClose={() => setInsp(null)} />}
      {addOpp && <AddTradeModal opp={addOpp.opp} market={addOpp.market} onClose={() => setAddOpp(null)} />}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-bold tracking-tight flex items-center gap-2"><Crosshair className="h-5 w-5 text-[#00C805]" /> Opportunities
          <span className="text-xs font-normal ml-2 px-2 py-0.5 rounded-sm bg-[#00C805]/15 text-[#00C805]">{market === "NFO" ? NFO_STRATEGY_LABEL : "MCX FUTURES · calendar spreads"}</span>
        </h1>
        {market === "MCX" && <div className="text-[11px] text-slate-500 mono" data-testid="mcx-opp-count"><span title={`Strategy ${data.strategy_version} · ${data.legacy_nse_excluded} legacy NSE rows excluded`}>{mcxShown.length} of {data.opportunities.length} opportunities shown · {data.universe_count} commodities scanned</span></div>}
      </div>

      <div className="term-panel rounded-sm p-2.5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">Market</span>
          <div className="flex rounded-sm overflow-hidden border border-[#2A2F3A]">
            {["MCX", "NFO"].map((m) => (
              <button key={m} onClick={() => setMarket(m)} data-testid={`market-select-${m.toLowerCase()}`}
                className={`px-2.5 py-1 font-bold ${market === m ? "bg-[#2563EB] text-white" : "bg-[#0A0C10] text-slate-500 hover:text-slate-200"}`}>
                {m} FUTURES
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">Strategy</span>
          {market === "MCX" ? (
            <span className="px-2 py-1 rounded-sm bg-[#1A1D24] border border-[#2A2F3A] font-semibold text-slate-200">MCX Futures · Hedging Far First</span>
          ) : (
            <span className="px-2 py-1 rounded-sm bg-[#2563EB]/15 border border-[#2563EB]/40 font-semibold text-[#5b9bff]" data-testid="nfo-strategy-label">{NFO_STRATEGY_LABEL}</span>
          )}
        </div>
        <OpportunityFilterBar filters={filters} onChange={setFilters} />
      </div>

      {market === "NFO" ? (
        <NfoOpportunities filters={filters} pins={pins} onAssign={(o) => setAddOpp({ opp: { ...o }, market: "NFO" })} />
      ) : (
        <>
          <PinnedOpportunities market="MCX" pins={pins.pins} rows={data.rows || data.opportunities}
            onTrade={(o) => setAddOpp({ opp: { ...o }, market: "MCX" })} onUnpin={(id) => pins.unpin(id).catch(() => {})} />
          <div className="term-panel rounded-sm overflow-auto" style={{ maxHeight: "calc(100vh - 280px)" }} {...held.bind}>
            {held.held && <div className="text-[10px] px-2 py-0.5 bg-[#b38f00]/20 text-[#b38f00]" data-testid="mcx-opp-held">{heldNote(held)}</div>}
            <table className="w-full text-xs border-collapse" data-testid="mcx-opp-table">
              <thead className="sticky top-0 z-10 bg-[#1A1D24] text-slate-400">
                <tr>{MCX_COLUMNS.map(([h, alignCls]) => (
                  <th key={h} title={SCENARIO_TITLES[h] || ECON_TITLE[h]} className={`px-1.5 py-2 whitespace-nowrap font-semibold ${alignCls}`}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {mcxShown.length === 0 && (
                  <tr><td colSpan={MCX_COLUMNS.length} className="px-4 py-6 text-center text-slate-600">
                    {data.opportunities.length === 0 ? "Waiting for live ticks / no qualifying opportunities."
                      : "No opportunities match the current Profit % Min / OI Min filters."}
                  </td></tr>
                )}
                {/* Rows arrive ranked by the spread scenario (net convergence P&L, return on Zerodha margin); filters only hide rows. */}
                {mcxShown.map((r) => {
                  const gate = addGate(r, { sessionOpen: mcxOpen });
                  // Two pairs per commodity may be listed; the Near+Mid row keeps the plain test id.
                  const rid = r.pair_selection === "MID_FAR" ? `${r.commodity}-MID_FAR` : r.commodity;
                  const active = (data.active_underlyings || []).filter((a) => a.underlying === r.commodity && a.market_segment === "MCX");
                  return (
                    <tr key={r.opportunity_id || r.commodity} className="gz-row mono" data-testid={`mcx-opp-row-${rid}`}>
                      <td className="px-1.5 py-1.5 text-left text-slate-500">{r.rank}</td>
                      <td className="px-1.5 py-1.5 text-left font-bold text-slate-100 whitespace-nowrap">{r.commodity}</td>
                      <td className="px-1.5 py-1.5 text-left"><PairCell row={r} testId={`mcx-pair-${rid}`} /></td>
                      <td className="px-1.5 py-1.5 text-left max-w-[9rem]"><ExactSymbol symbol={r.sell_leg} cls="text-[#FF6b62]" /></td>
                      <td className="px-1.5 py-1.5 text-left max-w-[9rem]"><ExactSymbol symbol={r.buy_leg} cls="text-[#5b9bff]" /></td>
                      <td className="px-1.5 py-1.5 text-right whitespace-nowrap"><SpreadEntryCell row={r} testId={`mcx-entry-spread-${rid}`} /></td>
                      <td className="px-1.5 py-1.5 text-right whitespace-nowrap"><ConvergeCell row={r} testId={`mcx-converge-${rid}`} /></td>
                      <td className="px-1.5 py-1.5 text-right whitespace-nowrap"><MarginCell row={r} testId={`mcx-margin-${rid}`} /></td>
                      <td className="px-1.5 py-1.5 text-right whitespace-nowrap"><ReturnCell row={r} testId={`mcx-roc-${rid}`} /></td>
                      <td className="px-1.5 py-1.5 text-right whitespace-nowrap">{num(r.near_ltp, 2)}</td>
                      <td className="px-1.5 py-1.5 text-right whitespace-nowrap">{num(r.far_ltp, 2)}</td>
                      <td className="px-1.5 py-1.5 text-right whitespace-nowrap"><OiCell value={r.near_oi} /></td>
                      <td className="px-1.5 py-1.5 text-right whitespace-nowrap"><OiCell value={r.far_oi} /></td>
                      <td className="px-1.5 py-1.5 text-right whitespace-nowrap"><SpreadCell row={r} testId={`mcx-spread-${rid}`} /></td>
                      <td className="px-1.5 py-1.5 text-left">
                        <PinToggle row={r} market="MCX" pinFor={pins.pinFor} pin={pins.pin} unpin={pins.unpin} />
                        <button disabled={gate.disabled} title={gate.title} onClick={() => setAddOpp({ opp: { ...r }, market: "MCX" })} data-testid={`add-trade-${rid}`}
                          className="text-[10px] font-bold px-2 py-1 rounded-sm bg-[#2563EB] whitespace-nowrap disabled:opacity-30 disabled:cursor-not-allowed">{gate.label}</button>
                        <RowStateBadge state={r.state} testId={`mcx-state-${rid}`} />
                        <ActiveTradeHint active={active} testId={`mcx-active-${rid}`} />
                      </td>
                      <td className="px-1.5 py-1.5 text-left">
                        <OpportunityInfo row={r} market="MCX" testId={`opp-info-${rid}`}
                          onWhy={() => setInsp(r.commodity)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-slate-600">Ranked by estimated NET convergence P&L (spread closing at zero, after estimated Zerodha charges and 1-tick slippage) on Zerodha's basket margin. BID/ASK = executable prices; LTP = indicative. Convergence is a scenario, not a guaranteed or maximum profit. The workbook projection is under Info. Only rows with live, valid market data can be added.</div>
        </>
      )}
    </div>
  );
}
